import { useState } from "react";
import { useAbac } from "react-abac";
import classNames from "classnames";
import { Button, Card, Form, FormControl } from "react-bootstrap";
import { useIntl } from "react-intl";
import { Permission } from "../../../../../config/abac.config";
import { useFlags } from "../../../../../config/featureFlags";
import { IProposal } from "../../../../../types/IProposal";
import BillAuthor from "../BillAuthor/BillAuthor";
import css from "./BillAddComment.module.css";

interface BillAddCommentProps {
  author: any;
  content?: string;
  onChangeProposalText: (e: any) => void;
  onSubmitProposal: () => Promise<void>;
  onPublishProposal: () => void;
  onDeleteProposal: () => void;
  onCancelButtonClick?: () => void;
  fetchProposalsInProgress: boolean;
  proposal?: IProposal;
  currentProposalId?: number;
  saveProposalStateInProgress?: string;
  savingProposalStateError?: string;
  billHasPassedDeadline: boolean;
}

const BillAddComment = (props: BillAddCommentProps) => {
  const {
    author,
    fetchProposalsInProgress,
    onCancelButtonClick,
    onChangeProposalText,
    onSubmitProposal,
    onPublishProposal,
    onDeleteProposal,
    content,
    proposal,
    currentProposalId,
    saveProposalStateInProgress,
    savingProposalStateError,
    billHasPassedDeadline,
  } = props;

  const intl = useIntl();
  const { userHasPermissions } = useAbac();
  const { DISPLAY_LIKUMI_CONTENT } = useFlags();

  const [isEditing, setEditing] = useState(false);

  const canModerate = userHasPermissions(Permission.MODERATE_PROPOSAL);
  const canPublish = userHasPermissions(Permission.PUBLISH_PROPOSAL, proposal);

  const isPublished = proposal?.isPublished;
  const isDraft = proposal && !isPublished;

  const isNewProposal = !proposal;
  const proposalId = proposal?.id || "temp_id";
  const isBusy =
    saveProposalStateInProgress && proposalId === currentProposalId;

  const showEditButton =
    (!billHasPassedDeadline && isDraft && !isEditing) ||
    (!isEditing && canModerate && isPublished);
  const showSaveButton =
    (!billHasPassedDeadline && isDraft && isEditing) ||
    (isEditing && canModerate && isPublished);
  const showPublishButton =
    !billHasPassedDeadline && isDraft && canPublish && DISPLAY_LIKUMI_CONTENT;
  const showPostButtons = !billHasPassedDeadline && isNewProposal;
  const showDeleteButton = !isNewProposal && canModerate;

  const EditPoposalButtonMaybe = () =>
    showEditButton ? (
      <Button
        className="d-flex mr-2"
        onClick={() => setEditing(true)}
        variant="outline-success"
        size="sm"
        type="button"
        disabled={fetchProposalsInProgress || isBusy}
      >
        <i className="fa fa-sm fa-edit"></i>
        {intl.formatMessage({ id: "BILL.ADD_COMMENT.EDIT_BTN" })}
      </Button>
    ) : null;

  const SavePoposalButtonMaybe = () =>
    showSaveButton ? (
      <Button
        className="d-flex mr-2"
        onClick={async (e) => {
          await onSubmitProposal();
          setEditing(false);
        }}
        variant="outline-success"
        size="sm"
        type="button"
        disabled={fetchProposalsInProgress || isBusy}
      >
        <i className="fa fa-sm fa-save"></i>
        {isBusy
          ? "Saving..."
          : intl.formatMessage({ id: "BILL.ADD_COMMENT.SAVE_BTN" })}
      </Button>
    ) : null;

  const PublishPoposalButtonMaybe = () =>
    showPublishButton ? (
      <Button
        onClick={onPublishProposal}
        className="align-self-center d-flex"
        variant="outline-primary"
        size="sm"
        type="button"
        disabled={fetchProposalsInProgress || isBusy}
      >
        {isBusy
          ? "Saving..."
          : intl.formatMessage({ id: "BILL.ADD_COMMENT.PUBLISH_BTN" })}
      </Button>
    ) : null;

  const DeletePoposalButtonMaybe = () =>
    showDeleteButton ? (
      <Button
        onClick={onDeleteProposal}
        className="align-self-center d-flex"
        variant="outline-danger"
        size="sm"
        type="button"
        disabled={fetchProposalsInProgress || isBusy}
      >
        {isBusy
          ? "Deleting..."
          : intl.formatMessage({ id: "BILL.ADD_COMMENT.DELETE_BTN" })}
      </Button>
    ) : null;

  const PlaceProposalButtonsMaybe = () =>
    showPostButtons ? (
      <>
        <Button
          onClick={onCancelButtonClick}
          className="align-self-center mr-2 d-flex"
          variant="outline-danger"
          size="sm"
        >
          {intl.formatMessage({ id: "AUTH.GENERAL.CANCEL_BUTTON" })}
        </Button>
        <Button
          onClick={onSubmitProposal}
          className="align-self-center d-flex"
          variant="outline-primary"
          size="sm"
          disabled={fetchProposalsInProgress || isBusy}
        >
          {isBusy
            ? "Saving..."
            : intl.formatMessage({ id: "BILL.ADD_COMMENT.SUBMIT_BTN" })}
        </Button>
      </>
    ) : null;

  const textAreaEnabled =
    isNewProposal || (isEditing && (isDraft || canModerate));

  return (
    <Card
      className={classNames(css.root, { [css.readonly]: !textAreaEnabled })}
    >
      <Card.Body>
        <Form as="div">
          <div className="d-flex">
            <BillAuthor author={author} />
            <FormControl
              as="textarea"
              readOnly={!textAreaEnabled}
              rows={3}
              className="p-0 border-0"
              placeholder={intl.formatMessage({
                id: "BILL.ADD_COMMENT.PLACE_HOLDER",
              })}
              value={content}
              onChange={onChangeProposalText}
            />

            <div className="proposalButtons">
              <PlaceProposalButtonsMaybe />
              <SavePoposalButtonMaybe />
              <EditPoposalButtonMaybe />
              <PublishPoposalButtonMaybe />
              <DeletePoposalButtonMaybe />
            </div>
          </div>
          {savingProposalStateError && (
            <div className={css.error}>
              {intl.formatMessage({ id: savingProposalStateError })}
            </div>
          )}
        </Form>
      </Card.Body>
    </Card>
  );
};

export default BillAddComment;
